import { useState, useEffect } from 'react';
import { IconButton, Button, Badge, Tooltip } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useAuthentication, useFirebaseReady } from 'hooks';
import { API_ENDPOINTS } from 'constants';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { databasePromise } from 'configuration';

const MAX_NAME_LENGTH = 100;
const ALLOWED_CONTEXT_KEYS = new Set([
  'dealId', 'dealTitle', 'restaurantId', 'restaurantName', 'orderId',
]);

// BUG-5 FIX: also match on otherUserId so we find the exact conversation
async function checkExistingConversation(currentUserId, otherUserId, contextType) {
  try {
    const db = await databasePromise;
    const q = query(
      collection(db, 'conversations'),
      where('participantIds', 'array-contains', currentUserId),
      where('context.type', '==', contextType),
    );
    const snapshot = await getDocs(q);
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      if (data.participantIds.includes(otherUserId)) {
        const unread = data.unreadCount?.[currentUserId] ?? 0;
        return { exists: true, unread };
      }
    }
    return { exists: false, unread: 0 };
  } catch {
    return { exists: false, unread: 0 };
  }
}

export function ChatButton({
  otherUser,
  context,
  contextData = {},
  disabled = false,
  label = null,
}) {
  const [navigating, setNavigating] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const { user, userFullName, userFirstName, userLastName } = useAuthentication();
  // BUG-4 FIX: gate Firestore read on Firebase auth being ready
  const firebaseReady = useFirebaseReady();

  const currentUser = {
    id: user?.uid,
    type: 'customer',
    name: userFullName
      || (userFirstName && userLastName ? `${userFirstName} ${userLastName}` : null)
      || user?.email
      || 'Unknown User',
  };

  // One-time check on mount — intentional. Running a live onSnapshot per
  // restaurant/deal page would open too many Firestore connections.
  // The NavbarChatButton in the top nav is the live unread indicator.
  useEffect(() => {
    if (!user?.uid || !otherUser?.id || !context || !firebaseReady) return;

    checkExistingConversation(user.uid, otherUser.id, context).then(({ exists, unread }) => {
      setHasExisting(exists);
      setUnreadCount(unread);
    });
  }, [user?.uid, otherUser?.id, context, firebaseReady]);

  const buildChatUrl = () => {
    const base = API_ENDPOINTS.CHAT.NEW;
    if (!otherUser) return base;

    // BUG-8 FIX: sanitise toUserName before passing in URL
    const safeName = (otherUser.name ?? '')
      .replace(/<[^>]*>/g, '')
      .trim()
      .slice(0, MAX_NAME_LENGTH) || 'Vendor';

    const params = new URLSearchParams();
    params.append('fromUserId', currentUser.id);
    params.append('toUserId', otherUser.id);
    params.append('toUserType', otherUser.type);
    params.append('toUserName', safeName);
    if (context) params.append('context', context);

    Object.entries(contextData).forEach(([key, value]) => {
      if (ALLOWED_CONTEXT_KEYS.has(key) && value !== null && value !== undefined) {
        params.append(key, String(value));
      }
    });

    return `${base}?${params.toString()}`;
  };

  const handleClick = () => {
    if (!currentUser?.id) {
      alert('Please sign in to start a chat.');
      return;
    }
    if (otherUser && !otherUser.id) {
      alert('Unable to start chat. Invalid recipient information.');
      return;
    }

    setNavigating(true);
    try {
      const chatUrl = buildChatUrl();
      window.open(chatUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error building chat URL:', error);
      alert('Failed to start chat. Please try again.');
    } finally {
      setNavigating(false);
    }
  };

  const tooltipLabel = !otherUser
    ? 'Open messages'
    : hasExisting
      ? unreadCount > 0
        ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`
        : 'Continue conversation'
      : 'Message vendor';

  const icon = !otherUser || !hasExisting
    ? <ChatBubbleOutlineIcon fontSize="small" />
    : <ChatIcon fontSize="small" />;

  if (label) {
    return (
      <Tooltip title={tooltipLabel} arrow>
        <span>
          <Button
            onClick={handleClick}
            disabled={disabled || navigating || !user}
            size="small"
            variant="outlined"
            startIcon={
              <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={9}>
                {icon}
              </Badge>
            }
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipLabel} arrow>
      <span>
        <IconButton
          onClick={handleClick}
          disabled={disabled || navigating || !user}
          size="small"
          color={hasExisting && unreadCount > 0 ? 'primary' : 'default'}
        >
          <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={9}>
            {icon}
          </Badge>
        </IconButton>
      </span>
    </Tooltip>
  );
}
